import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Clock, 
  ListTodo, 
  Target, 
  Plus, 
  Check, 
  Trash2, 
  Calendar,
  ChevronLeft,
  ChevronRight,
  Filter
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WorkPlanning = ({ user }) => {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [workPlan, setWorkPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newItemContent, setNewItemContent] = useState({ next_24hr_completion: '', work_list: '', envisioned_completions: '' });
  const [newItemDeadline, setNewItemDeadline] = useState({ work_list: '' });
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [filteredPlans, setFilteredPlans] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const fetchWorkPlan = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/work-plans/${selectedDate}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWorkPlan(response.data);
    } catch (error) {
      console.error('Failed to fetch work plan:', error);
      toast.error('Failed to fetch work plan');
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  const fetchFilteredPlans = useCallback(async () => {
    if (!dateFrom && !dateTo) {
      setFilteredPlans([]);
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      
      const response = await axios.get(`${API_URL}/work-plans?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFilteredPlans(response.data);
    } catch (error) {
      console.error('Failed to fetch work plans:', error);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    fetchWorkPlan();
  }, [fetchWorkPlan]);

  useEffect(() => {
    if (showHistory) {
      fetchFilteredPlans();
    }
  }, [showHistory, fetchFilteredPlans]);

  const addItem = async (section) => {
    const content = newItemContent[section].trim();
    if (!content) {
      toast.error('Please enter some content');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const itemData = { section, content };
      // Add deadline for work_list (Priorities) section
      if (section === 'work_list' && newItemDeadline.work_list) {
        itemData.deadline = newItemDeadline.work_list;
      }
      await axios.post(`${API_URL}/work-plans/${selectedDate}/items`, 
        itemData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewItemContent({ ...newItemContent, [section]: '' });
      if (section === 'work_list') {
        setNewItemDeadline({ ...newItemDeadline, work_list: '' });
      }
      fetchWorkPlan();
      toast.success('Item added successfully');
    } catch (error) {
      toast.error('Failed to add item');
    }
  };

  const toggleItemComplete = async (itemId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/work-plans/${selectedDate}/items/${itemId}?completed=${!currentStatus}`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchWorkPlan();
    } catch (error) {
      toast.error('Failed to update item');
    }
  };

  const deleteItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/work-plans/${selectedDate}/items/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchWorkPlan();
      toast.success('Item deleted');
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const navigateDate = (direction) => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + direction);
    setSelectedDate(date.toISOString().split('T')[0]);
  };

  const formatDisplayDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    const dateOnly = new Date(dateStr + 'T00:00:00');
    dateOnly.setHours(0, 0, 0, 0);
    
    if (dateOnly.getTime() === today.getTime()) return 'Today';
    if (dateOnly.getTime() === tomorrow.getTime()) return 'Tomorrow';
    if (dateOnly.getTime() === yesterday.getTime()) return 'Yesterday';
    
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const sections = [
    { key: 'next_24hr_completion', label: 'Next 24 Hours', icon: Clock, color: '#EF4444', description: 'What you will complete in the next 24 hours', hasDeadline: false },
    { key: 'work_list', label: 'Priorities', icon: ListTodo, color: '#3B82F6', description: 'Your priority items with deadlines', hasDeadline: true },
    { key: 'envisioned_completions', label: 'Envisioned Completions', icon: Target, color: '#10B981', description: 'What you envision completing', hasDeadline: false }
  ];

  const renderSection = (section) => {
    const items = workPlan?.[section.key] || [];
    const completedCount = items.filter(i => i.completed).length;
    
    return (
      <div 
        key={section.key}
        style={{
          background: 'var(--bg-secondary)',
          borderRadius: '12px',
          border: '1px solid var(--border-color)',
          overflow: 'hidden'
        }}
        data-testid={`section-${section.key}`}
      >
        {/* Section Header */}
        <div style={{
          padding: '16px 20px',
          background: `${section.color}10`,
          borderBottom: '1px solid var(--border-color)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              background: `${section.color}20`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <section.icon size={20} color={section.color} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>
                {section.label}
              </h3>
              <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-tertiary)' }}>
                {section.description}
              </p>
            </div>
          </div>
          <div style={{
            padding: '4px 12px',
            borderRadius: '20px',
            background: section.color,
            color: 'white',
            fontSize: '12px',
            fontWeight: '600'
          }}>
            {completedCount}/{items.length}
          </div>
        </div>

        {/* Items List */}
        <div style={{ padding: '16px' }}>
          {items.length === 0 ? (
            <p style={{ textAlign: 'center', color: 'var(--text-tertiary)', margin: '20px 0', fontSize: '14px' }}>
              No items yet. Add your first item below.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
              {items.map(item => (
                <div 
                  key={item.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px',
                    background: item.completed ? `${section.color}08` : 'var(--bg-primary)',
                    borderRadius: '8px',
                    border: `1px solid ${item.completed ? section.color + '30' : 'var(--border-color)'}`
                  }}
                  data-testid={`item-${item.id}`}
                >
                  <button
                    onClick={() => toggleItemComplete(item.id, item.completed)}
                    style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '6px',
                      border: `2px solid ${item.completed ? section.color : 'var(--border-color)'}`,
                      background: item.completed ? section.color : 'transparent',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                    data-testid={`toggle-${item.id}`}
                  >
                    {item.completed && <Check size={14} color="white" />}
                  </button>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{
                      fontSize: '14px',
                      color: item.completed ? 'var(--text-tertiary)' : 'var(--text-primary)',
                      textDecoration: item.completed ? 'line-through' : 'none'
                    }}>
                      {item.content}
                    </span>
                    {item.deadline && (
                      <div style={{ 
                        fontSize: '11px', 
                        color: new Date(item.deadline) < new Date() && !item.completed ? '#EF4444' : 'var(--text-tertiary)', 
                        marginTop: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <Calendar size={10} />
                        Due: {new Date(item.deadline).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => deleteItem(item.id)}
                    style={{
                      padding: '6px',
                      background: 'rgba(239, 68, 68, 0.1)',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      color: '#EF4444',
                      flexShrink: 0
                    }}
                    data-testid={`delete-${item.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Add Item Input */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="text"
                placeholder={`Add to ${section.label.toLowerCase()}...`}
                value={newItemContent[section.key]}
                onChange={(e) => setNewItemContent({ ...newItemContent, [section.key]: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && !section.hasDeadline && addItem(section.key)}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  background: 'var(--bg-primary)',
                  color: 'var(--text-primary)',
                  fontSize: '14px'
                }}
                data-testid={`input-${section.key}`}
              />
              <button
                onClick={() => addItem(section.key)}
                style={{
                  padding: '12px 16px',
                  background: section.color,
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontWeight: '600',
                  fontSize: '14px'
                }}
                data-testid={`add-btn-${section.key}`}
              >
                <Plus size={18} />
                Add
              </button>
            </div>
            {/* Deadline picker for Priorities section */}
            {section.hasDeadline && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Calendar size={16} color={section.color} />
                <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Deadline:</span>
                <input
                  type="date"
                  value={newItemDeadline.work_list}
                  onChange={(e) => setNewItemDeadline({ ...newItemDeadline, work_list: e.target.value })}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    background: 'var(--bg-primary)',
                    color: 'var(--text-primary)',
                    fontSize: '13px'
                  }}
                  data-testid="deadline-input-work_list"
                />
                <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>(optional)</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div className="loading-spinner"></div>
        <p style={{ marginTop: '16px', color: 'var(--text-secondary)' }}>Loading work plan...</p>
      </div>
    );
  }

  return (
    <div data-testid="work-planning">
      {/* Header with Date Navigation */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            onClick={() => navigateDate(-1)}
            style={{
              padding: '8px',
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              cursor: 'pointer',
              color: 'var(--text-primary)'
            }}
          >
            <ChevronLeft size={20} />
          </button>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Calendar size={24} color="#7077FE" />
            <div>
              <div style={{ fontSize: '20px', fontWeight: '700', color: 'var(--text-primary)' }}>
                {formatDisplayDate(selectedDate)}
              </div>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: 'var(--text-tertiary)',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              />
            </div>
          </div>
          
          <button
            onClick={() => navigateDate(1)}
            style={{
              padding: '8px',
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              cursor: 'pointer',
              color: 'var(--text-primary)'
            }}
          >
            <ChevronRight size={20} />
          </button>
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])}
            style={{
              padding: '10px 20px',
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              cursor: 'pointer',
              color: 'var(--text-primary)',
              fontWeight: '500',
              fontSize: '14px'
            }}
          >
            Today
          </button>
          <button
            onClick={() => setShowHistory(!showHistory)}
            style={{
              padding: '10px 20px',
              background: showHistory ? '#7077FE' : 'var(--bg-secondary)',
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              cursor: 'pointer',
              color: showHistory ? 'white' : 'var(--text-primary)',
              fontWeight: '500',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            data-testid="toggle-history"
          >
            <Filter size={16} />
            {showHistory ? 'Hide History' : 'View History'}
          </button>
        </div>
      </div>

      {/* History Filter Section */}
      {showHistory && (
        <div style={{
          padding: '16px 20px',
          background: 'var(--bg-secondary)',
          borderRadius: '12px',
          border: '1px solid var(--border-color)',
          marginBottom: '24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: '600', color: 'var(--text-primary)' }}>Filter by Date:</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  background: 'var(--bg-primary)',
                  color: 'var(--text-primary)'
                }}
              />
              <span style={{ color: 'var(--text-tertiary)' }}>to</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  background: 'var(--bg-primary)',
                  color: 'var(--text-primary)'
                }}
              />
            </div>
            <button
              onClick={fetchFilteredPlans}
              className="gradient-btn"
              style={{ padding: '8px 16px' }}
            >
              Apply Filter
            </button>
          </div>

          {/* Filtered Results */}
          {filteredPlans.length > 0 && (
            <div style={{ marginTop: '20px' }}>
              <h4 style={{ margin: '0 0 12px 0', color: 'var(--text-primary)' }}>
                Work Plans ({filteredPlans.length})
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '400px', overflowY: 'auto' }}>
                {filteredPlans.map(plan => {
                  const totalItems = (plan.next_24hr_completion?.length || 0) + 
                                    (plan.work_list?.length || 0) + 
                                    (plan.envisioned_completions?.length || 0);
                  const completedItems = 
                    (plan.next_24hr_completion?.filter(i => i.completed).length || 0) +
                    (plan.work_list?.filter(i => i.completed).length || 0) +
                    (plan.envisioned_completions?.filter(i => i.completed).length || 0);
                  
                  return (
                    <div 
                      key={plan.id}
                      onClick={() => { setSelectedDate(plan.date); setShowHistory(false); }}
                      style={{
                        padding: '16px',
                        background: 'var(--bg-primary)',
                        borderRadius: '8px',
                        border: '1px solid var(--border-color)',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
                          {new Date(plan.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '4px' }}>
                          {plan.next_24hr_completion?.length || 0} in Next 24h • 
                          {plan.work_list?.length || 0} in Work List • 
                          {plan.envisioned_completions?.length || 0} Envisioned
                        </div>
                      </div>
                      <div style={{
                        padding: '6px 12px',
                        borderRadius: '20px',
                        background: completedItems === totalItems && totalItems > 0 ? '#10B98120' : '#7077FE20',
                        color: completedItems === totalItems && totalItems > 0 ? '#10B981' : '#7077FE',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {completedItems}/{totalItems} completed
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Work Plan Sections */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '20px' }}>
        {sections.map(section => renderSection(section))}
      </div>
    </div>
  );
};

export default WorkPlanning;
